import type { Appearance, EquipSlot, SectId, StatBlock, StatusId } from '@jianghu/game-data';

/** Server → client messages (authored by the trusted server; documented in docs/networking/PROTOCOL.md). */

export const PROTOCOL_VERSION = 1;

export type EntityKind = 'player' | 'monster' | 'npc' | 'loot';

/** Coarse animation/behaviour state carried in every snapshot. */
export const ENTITY_STATE = {
  idle: 0,
  moving: 1,
  acting: 2,
  dead: 3,
  returning: 4,
  stunned: 5,
} as const;
export type EntityStateCode = (typeof ENTITY_STATE)[keyof typeof ENTITY_STATE];

export interface PlayerVisualDTO {
  sect: SectId;
  appearance: Appearance;
  weapon: string | null;
  armor: string | null;
}

export interface EntitySpawnDTO {
  id: number;
  k: EntityKind;
  name: string;
  title?: string;
  level?: number;
  x: number;
  z: number;
  yaw: number;
  hp?: number;
  maxHp?: number;
  player?: PlayerVisualDTO;
  monsterId?: string;
  npcId?: string;
  loot?: { mine: boolean; rarity: string };
  /** Quest marker for this viewer: '!' quest available, '?' ready to turn in, '…' in progress. */
  marker?: '!' | '?' | '…' | '';
  state?: EntityStateCode;
}

/** [id, x, z, yaw, hp, state, statusBits] */
export type EntitySnapshot = [number, number, number, number, number, number, number];

export interface SelfSnapshot {
  x: number;
  z: number;
  fx: number;
  fz: number;
  sta: number;
  dashT: number;
  dashX: number;
  dashZ: number;
  dashCd: number;
  rt: number;
  sl: boolean;
  cs: number;
  ct: number;
  hp: number;
  mhp: number;
  mp: number;
  mmp: number;
  sm: number;
  ca: boolean;
  dead: boolean;
  st: [StatusId, number][];
}

export interface DerivedStatsDTO {
  level: number;
  maxHp: number;
  maxMp: number;
  atk: number;
  qiAtk: number;
  def: number;
  crit: number;
  evasion: number;
  accuracy: number;
  power: number;
  attributes: { str: number; agi: number; vit: number; ene: number };
}

export interface InvItemDTO {
  id: string;
  defId: string;
  qty: number;
  bagSlot: number | null;
  equipSlot: EquipSlot | null;
  stats: StatBlock;
}

export interface QuestObjectiveDTO {
  id: string;
  label: string;
  current: number;
  required: number;
}

export interface QuestDTO {
  id: string;
  status: 'active' | 'completed';
  objectives: QuestObjectiveDTO[];
  ready: boolean;
}

export interface DialogOptionDTO {
  op: 'accept' | 'turnin' | 'shop' | 'talk' | 'close';
  quest?: string;
  label: string;
}

export interface ShopDTO {
  npc: number;
  shopId: string;
  name: string;
  sells: { itemId: string; price: number }[];
  buysCategories: string[];
}

export interface CharacterSelfDTO {
  characterId: string;
  entityId: number;
  name: string;
  sect: SectId;
  level: number;
  xp: number;
  xpNext: number;
  coins: number;
  appearance: Appearance;
  isAdmin: boolean;
}

export type ServerMessage =
  | {
      t: 'welcome';
      protocol: number;
      serverTime: number;
      zoneId: string;
      self: CharacterSelfDTO;
      stats: DerivedStatsDTO;
      inventory: InvItemDTO[];
      quests: QuestDTO[];
      cooldowns: [string, number][];
      skills: string[];
      move: SelfSnapshot;
      lastSeq: number;
    }
  | { t: 's'; st: number; ack: number; me: SelfSnapshot; e: EntitySnapshot[] }
  | { t: 'spawn'; e: EntitySpawnDTO[] }
  | { t: 'despawn'; ids: number[] }
  | {
      t: 'cast';
      src: number;
      sk: string;
      step: number;
      tg?: number;
      ax: number;
      az: number;
      castMs: number;
      seq?: number;
    }
  | { t: 'castRej'; seq: number; sk: string; reason: string }
  | {
      t: 'proj';
      src: number;
      sk: string;
      x: number;
      z: number;
      dx: number;
      dz: number;
      speed: number;
      dist: number;
      width: number;
    }
  | {
      t: 'hit';
      src: number;
      tgt: number;
      amt: number;
      kind: 'hit' | 'crit' | 'evade' | 'dodge' | 'heal';
      sk: string;
      kb?: [number, number];
    }
  | {
      t: 'telegraph';
      src: number;
      sk: string;
      x: number;
      z: number;
      dx: number;
      dz: number;
      ms: number;
      shape: 'arc' | 'circle';
      radius: number;
      halfAngleDeg: number;
    }
  | { t: 'death'; id: number; killer?: number }
  | { t: 'revive'; id: number }
  | { t: 'xp'; gained: number; xp: number; level: number; xpNext: number }
  | { t: 'level'; id: number; level: number }
  | { t: 'stats'; stats: DerivedStatsDTO }
  | { t: 'skills'; skills: string[] }
  | { t: 'coins'; coins: number }
  | { t: 'inv'; items: InvItemDTO[]; coins: number }
  | { t: 'quests'; quests: QuestDTO[] }
  | { t: 'markers'; m: [number, '!' | '?' | '…' | ''][] }
  | {
      t: 'dialog';
      npc: number;
      npcId: string;
      name: string;
      title: string;
      lines: string[];
      options: DialogOptionDTO[];
    }
  | { t: 'shop'; shop: ShopDTO }
  | { t: 'toast'; kind: 'info' | 'error' | 'loot' | 'quest' | 'level'; text: string }
  | { t: 'chat'; ch: 'zone' | 'system'; from?: string; fromId?: number; text: string; at: number }
  | { t: 'pong'; c: number; s: number }
  | { t: 'tele'; x: number; z: number }
  | { t: 'cd'; cds: [string, number][] }
  | { t: 'area'; id: string; name: string }
  | { t: 'kick'; reason: string };
